import { useMemo, useState, type ReactElement } from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  colorSchemeDark,
  colorSchemeLight,
  type ColDef,
} from 'ag-grid-community';
import { Button, useTheme } from '@csa/ui';

/**
 * The results grid.
 *
 * AG Grid Community, specifically. Enterprise is a per-developer licence
 * for features this console does not need -- pivoting, grouping,
 * server-side row models. Community's client-side row model with DOM
 * virtualisation renders fifty thousand rows without difficulty, and fifty
 * thousand is the cap anyway.
 */

ModuleRegistry.registerModules([AllCommunityModule]);

/**
 * The hard ceiling on rows held in the browser.
 *
 * Not a performance limit -- the grid virtualises and would cope with more.
 * It is a *memory* limit: each row is a JavaScript array retained for as
 * long as the tab is open, and a support engineer with four console tabs
 * open during an incident is the normal case, not the edge case. Anything
 * larger belongs in a batch export, which streams to S3 and never touches
 * the browser.
 */
export const GRID_ROW_CAP = 50_000;

export interface ResultColumnView {
  readonly name: string;
  readonly dataType: string;
}

export interface ResultGridProps {
  readonly columns: readonly ResultColumnView[];
  readonly rows: readonly (readonly unknown[])[];
  readonly truncated: boolean;
  /**
   * Set when the grid is inside something that has already decided its
   * height -- a splitter pane, say. See the comment on the floor below: in
   * that case the floor stops being a safety net and starts being a bug,
   * because a 24rem minimum inside a pane the user has dragged to 8rem
   * overflows the pane instead of shrinking with it.
   */
  readonly fills?: boolean;
}

export function ResultGrid({
  columns,
  rows,
  truncated,
  fills = false,
}: ResultGridProps): ReactElement {
  const { resolved } = useTheme();

  /**
   * On by default.
   *
   * The opposite was the obvious choice -- it is what every database
   * client does -- but it optimises for scanning a wide result, and the
   * common case here is a support engineer reading a handful of rows
   * closely. A value cut off at the column edge is the thing they came for
   * and cannot see, so the default favours reading over scanning; the
   * toggle restores one line per row for the wide results where scanning
   * is the point.
   */
  const [wrap, setWrap] = useState(true);

  /**
   * Rows arrive as arrays and are converted to objects keyed by index, not
   * by column name. Two columns in a result set can share a name -- a join
   * selecting `a.id, b.id` is routine -- and keying by name would silently
   * drop one of them. The header shows the name; the key is positional.
   */
  const rowData = useMemo(
    () =>
      rows.map((row) => {
        const record: Record<string, unknown> = {};
        for (let i = 0; i < row.length; i += 1) record[`c${i}`] = row[i];
        return record;
      }),
    [rows],
  );

  /**
   * An expression without an alias -- `select @@VERSION`, `select count(*)`
   * -- comes back with no name at all, and a blank header gives no way to
   * tell which column is which or that the column is even there. The
   * position is the only name it has, so that is what is shown; the
   * tooltip says why.
   */
  const columnDefs = useMemo<ColDef[]>(
    () =>
      columns.map((column, index) => ({
        field: `c${index}`,
        headerName: column.name.trim() === '' ? `(column ${index + 1})` : column.name,
        headerTooltip:
          column.name.trim() === ''
            ? `Unnamed expression in position ${index + 1} — ${column.dataType}. Add an alias to name it.`
            : `${column.name} — ${column.dataType}`,
        sortable: true,
        filter: true,
        resizable: true,
        wrapText: wrap,
        autoHeight: wrap,
        /**
         * Wrapping needs the column to have a width to wrap inside, so the
         * columns share the viewport. Unwrapped they are sized to their
         * contents instead, capped so that one wide column cannot push
         * every other column off the screen.
         */
        ...(wrap ? { flex: 1 } : { maxWidth: 1_200 }),
        // Values are rendered as text, never as HTML. A result cell is
        // untrusted content from a production database, and a grid that
        // interpreted markup in it would be a stored-XSS sink reachable by
        // anyone who can write a row anywhere in the estate.
        valueFormatter: (params) => formatCell(params.value),
        cellClass: (params) =>
          params.value === null || params.value === undefined
            ? 'csa-cell-null'
            : typeof params.value === 'number' || typeof params.value === 'bigint'
              ? 'csa-cell-number'
              : '',
      })),
    [columns, wrap],
  );

  const theme = useMemo(
    () =>
      themeQuartz.withPart(resolved === 'dark' ? colorSchemeDark : colorSchemeLight).withParams({
        // Read from the design tokens so the grid follows the theme
        // toggle rather than maintaining a second colour system.
        accentColor: 'rgb(var(--brand))',
        backgroundColor: 'rgb(var(--surface))',
        foregroundColor: 'rgb(var(--text))',
        headerBackgroundColor: 'rgb(var(--surface-raised))',
        borderColor: 'rgb(var(--border))',
        fontFamily: 'inherit',
        fontSize: '13px',
        // Compact rows: the point of this grid is to see as much of the
        // result at once as possible.
        rowHeight: 30,
        headerHeight: 34,
      }),
    [resolved],
  );

  /**
   * The floor is what makes the grid visible at all.
   *
   * `domLayout="normal"` means the grid fills its parent, and `flex-1`
   * only claims *free* space. The app shell puts every page inside a plain
   * content-sized block, so there is no free space to claim: this element
   * resolved to zero height and the result rendered as nothing but a row
   * count. A `min-height` is the one thing flexbox will not collapse, so
   * the grid has a usable height no matter what the chain above it does.
   *
   * Half the viewport where there is room, never less than 24rem, and
   * `flex-1` still lets it grow if a future layout does give it space.
   *
   * `fills` is that future layout arriving. A parent that has already sized
   * itself -- a splitter pane with a height the user chose -- does give the
   * grid space, and there the floor is actively wrong: it would stop the
   * grid shrinking when the pane is dragged smaller, so the grid would
   * overflow the pane rather than fit it. `min-h-0` is the opposite
   * instruction and the one a sized parent needs.
   */
  return (
    <div className={`flex flex-1 flex-col ${fills ? 'min-h-0' : 'min-h-[max(24rem,50vh)]'}`}>
      <div className="flex items-center justify-end border-b border-line px-2 py-1">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => setWrap((previous) => !previous)}
          aria-pressed={wrap}
          title={
            wrap
              ? 'Show one line per row and scroll sideways'
              : 'Wrap long values onto several lines'
          }
        >
          {wrap ? 'No wrap' : 'Wrap text'}
        </Button>
      </div>

      {truncated ? (
        <p
          className="border-b border-warning-border bg-warning-bg px-3 py-1.5 text-xs text-warning-fg"
          role="status"
        >
          Showing the first {rows.length.toLocaleString()} rows. The result was larger — run this as
          a batch export to get all of it.
        </p>
      ) : null}

      {/*
        The grid is positioned absolutely inside this box rather than
        simply placed in it.

        AG Grid gives its own root `height: 100%`, and a percentage height
        resolves against the containing block only when that block's height
        is *definite*. An element sized by `flex-grow` is not definite for
        this purpose, so the grid resolved 100% against nothing, fell back
        to its content height -- and its rows are absolutely positioned, so
        that height is 1px. The result was a grid that reported the right
        row count, built the right cells, and clipped all of them.

        An absolutely positioned box with `inset-0` takes its height from
        the containing block directly, which is definite, so the chain
        resolves.
      */}
      <div className="relative min-h-0 flex-1">
        <div className="absolute inset-0">
          <AgGridReact
            theme={theme}
            rowData={rowData}
            columnDefs={columnDefs}
            /**
             * Text selection, not cell/range selection. `cellSelection` is
             * an Enterprise feature and this is the Community build, so it
             * was only ever ignored; selecting the text is what people
             * actually do with a result anyway -- they copy a value out.
             */
            suppressFieldDotNotation
            enableCellTextSelection
            animateRows={false}
            // A row count this size makes animation actively unpleasant and
            // costs frames on the scroll that matters.
            rowBuffer={20}
            defaultColDef={{ minWidth: 90, flex: 1 }}
            // Announced to assistive technology; without it the grid is an
            // unlabelled region.
            gridId="csa-results"
            domLayout="normal"
            /**
             * Sizes each column to what is actually in it, measured from
             * the rendered rows only, so this stays cheap at the row cap.
             * Skipped while wrapping, where the columns share the width
             * instead.
             */
            autoSizeStrategy={wrap ? undefined : { type: 'fitCellContents' }}
          />
        </div>
      </div>
    </div>
  );
}

/**
 * Renders a cell.
 *
 * The same reasoning as the CSV encoder on the server, and deliberately the
 * same decisions: a `null` shown as empty is indistinguishable from an
 * empty string, which matters when someone is deciding whether a column is
 * populated. So null is shown explicitly here -- unlike in CSV, where the
 * file might be loaded back into a table and a literal "NULL" would become
 * a string.
 */
function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '∅';
  if (value instanceof Date) return value.toISOString();
  if (typeof value === 'bigint') return value.toString();
  if (typeof value === 'boolean') return value ? 'true' : 'false';
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}
